/**
 * Hardens React DOM mutations against browser extensions (esp. Google Translate)
 * and React 19 + Radix portal races that cause:
 *   NotFoundError: Failed to execute 'removeChild' on 'Node'
 * See: https://github.com/facebook/react/issues/17256
 *
 * Must run before any React commit. Prefer the inline script in public/index.html;
 * this module re-applies the same patch for HMR / non-HTML entry paths.
 */
function installDomSafetyPatch() {
  if (typeof Node === 'undefined' || !Node.prototype) return;

  const proto = Node.prototype;

  if (!proto.removeChild.__vmkPatched) {
    const originalRemoveChild = proto.removeChild;
    function removeChild(child) {
      if (child == null) return child;
      if (child.parentNode !== this) {
        return child;
      }
      try {
        return originalRemoveChild.call(this, child);
      } catch (err) {
        if (err && (err.name === 'NotFoundError' || err.code === 8)) return child;
        throw err;
      }
    }
    removeChild.__vmkPatched = true;
    proto.removeChild = removeChild;
  }

  if (!proto.insertBefore.__vmkPatched) {
    const originalInsertBefore = proto.insertBefore;
    function insertBefore(newNode, referenceNode) {
      if (referenceNode && referenceNode.parentNode !== this) {
        try {
          return originalInsertBefore.call(this, newNode, null);
        } catch (err) {
          if (err && (err.name === 'NotFoundError' || err.code === 8)) return newNode;
          throw err;
        }
      }
      try {
        return originalInsertBefore.call(this, newNode, referenceNode);
      } catch (err) {
        if (err && (err.name === 'NotFoundError' || err.code === 8)) {
          try {
            return originalInsertBefore.call(this, newNode, null);
          } catch {
            return newNode;
          }
        }
        throw err;
      }
    }
    insertBefore.__vmkPatched = true;
    proto.insertBefore = insertBefore;
  }

  if (!proto.replaceChild.__vmkPatched) {
    const originalReplaceChild = proto.replaceChild;
    function replaceChild(newChild, oldChild) {
      if (oldChild && oldChild.parentNode !== this) {
        try {
          return originalReplaceChild.call(this, newChild, oldChild);
        } catch {
          try {
            this.appendChild(newChild);
          } catch {
            /* ignore */
          }
          return oldChild;
        }
      }
      try {
        return originalReplaceChild.call(this, newChild, oldChild);
      } catch (err) {
        if (err && (err.name === 'NotFoundError' || err.code === 8)) return oldChild;
        throw err;
      }
    }
    replaceChild.__vmkPatched = true;
    proto.replaceChild = replaceChild;
  }
}

installDomSafetyPatch();

export function patchReactDomSafety() {
  installDomSafetyPatch();
}
